import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import {
  Box,
  Card,
  CardMedia,
  FormControlLabel,
  FormGroup,
  Link,
  Switch,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  AccountBalance,
  AccountCircle,
  Audiotrack,
  Label,
  Place,
  Videocam,
} from '@mui/icons-material';

import { overrideRessource } from 'generic/utils/utils';
import { documentPropType } from 'generic/core/qes/proptypes';
import ExpandableTagList from 'generic/components/ui/ExpandableTagList';
import MediaWithTranscription from 'generic/components/ui/MediaWithTranscription';
import ImgLoader from 'generic/components/ui/ImgLoader';
import ImageNotFound from 'generic/components/ui/ImageNotFound';

import QES_CONSTANTS from 'generic/core/qes/constants';
import { prettyPrintSize } from 'generic/utils/mathUtils';
import JsxJoinerWithSeparator from 'generic/components/ui/JsxJoinerWithSeparator';

const { default: useDocumentFields } = overrideRessource('core/hooks/useDocumentFields', true);
const { CLICK_ON_DOCS_TITLE_BEHAVIOR } = QES_CONSTANTS;

const useStyles = makeStyles((theme) => ({
  itemImage: {
    objectFit: 'cover',
    display: 'block',
    borderRadius: '4px',
    boxSizing: 'border-box',
    border: '1px solid',
    borderColor: theme.palette.divider,
  },
}));

const Document = ({
  disableTagsRefine,
  displayTitle,
  document,
  handleGetDocumentComplete,
  handleToggleTranslatedTextDisplay,
  isAnnotatedTextDisplayed,
  handleToggleAnnotatedTextDisplay,
  isCompleteDisplay,
  isTranslatedTextDisplayed,
  patchDocumentInComplete,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    attachedFileURL,
    faces,
    fileSize,
    frames,
    htmlWithAnnotations,
    lang,
    linkout,
    textOriginal,
    textTranslated,
    title,
    transcriptionJSON,
    isAudio,
    isImage,
    isVideo,
    isPdf,
  } = useDocumentFields(document);

  const { qesdocument } = document;

  let titleLinkProps;
  if (CLICK_ON_DOCS_TITLE_BEHAVIOR === 'newTab') {
    titleLinkProps = {
      component: Link,
      href: linkout,
      rel: 'noopener',
      target: '_blank',
      underline: 'none',
    };
  } else if (CLICK_ON_DOCS_TITLE_BEHAVIOR === 'complete') {
    titleLinkProps = {
      component: 'span',
      onClick: handleGetDocumentComplete,
    };
  }

  let tags;
  if (isCompleteDisplay) {
    tags = [
      { fieldName: 'QES_Company', icon: AccountBalance },
      { fieldName: 'QES_Person', icon: AccountCircle },
      { fieldName: 'QES_Location', icon: Place },
      { fieldName: 'QES_ConceptCategorized', icon: Label },
    ];
  } else {
    tags = [
      { fieldName: 'QES_Company_display', icon: AccountBalance },
      { fieldName: 'QES_Person_display', icon: AccountCircle },
      { fieldName: 'QES_Location_display', icon: Place },
      { fieldName: 'QES_ConceptCategorized_display', icon: Label },
    ];
  }

  const shouldDisplayHtmlWithAnnotations = htmlWithAnnotations
    && isAnnotatedTextDisplayed
    && (!textTranslated || !isTranslatedTextDisplayed);
  const shouldDisplayTextTranslated = textTranslated && isTranslatedTextDisplayed;
  const shouldDisplayTextOriginal = !shouldDisplayHtmlWithAnnotations && !shouldDisplayTextTranslated;
  const textSx = {
    mt: 2,
    whiteSpace: 'pre-wrap',
    lineHeight: '30px',
    clear: 'both',
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow="1"
      width="100%"
      overflow="auto"
      sx={{ wordBreak: 'break-word' }}
    >
      {displayTitle && (
        <Typography
          variant="h6"
          component="span"
          fontSize="1.2rem"
          margin="6px 0"
          display="block"
        >
          <Box
            {...titleLinkProps}
            color="text.primary"
            sx={{ cursor: 'pointer' }}
          >
            {title}
          </Box>
        </Typography>
      )}

      <Box display="flex" alignItems="flex-start">
        <Box flexGrow="1">
          {isImage && !isCompleteDisplay && (
            <Box mr={1} mb="2px" sx={{ float: 'left' }}>
              <ImgLoader
                src={attachedFileURL}
                fallbackComponent={(
                  <ImageNotFound
                    height={60}
                    width={100}
                  />
                )}
                className={classes.itemImage}
                skeletonClassName={classes.itemImage}
                height={60}
                width={100}
                loading="lazy"
              />
            </Box>
          )}
          <Box lineHeight="1.3rem">
            <Box display="flex" flexWrap="wrap" mb={0.5}>
              <JsxJoinerWithSeparator items={[
                lang && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.neutral"
                    className="item"
                  >
                    {lang}
                  </Typography>
                ),
                fileSize && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.neutral"
                    className="item"
                  >
                    {prettyPrintSize(+fileSize)}
                  </Typography>
                ),
                attachedFileURL && (
                  <Link
                    href={attachedFileURL}
                    rel="noopener"
                    target="_blank"
                    underline="none"
                    sx={{ display: 'flex' }}
                  >
                    <Typography component="span" variant="body2">{t('document.open_document')}</Typography>
                  </Link>
                ),
                !isCompleteDisplay && (isAudio || isVideo) && (
                  <Fragment>
                    {isVideo && <Videocam fontSize="small" />}
                    {isAudio && <Audiotrack fontSize="small" />}
                  </Fragment>
                ),
              ]}
              />
            </Box>
            <Box mt={1}>
              <ExpandableTagList
                disableRefine={disableTagsRefine}
                qesdocument={qesdocument}
                tags={tags}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {isCompleteDisplay && (
        <Fragment>
          {!isPdf && textTranslated && (
            <FormGroup>
              <FormControlLabel
                checked={isTranslatedTextDisplayed}
                control={<Switch onChange={handleToggleTranslatedTextDisplay} />}
                label={t('results.switch_translated_text')}
                sx={{ pl: '11px' }}
              />
            </FormGroup>
          )}
          {_.isEmpty(transcriptionJSON) && (
            <Fragment>
              {isImage && (
                <Box sx={{ my: 3 }}>
                  <ImgLoader
                    src={attachedFileURL}
                    fallbackComponent={(
                      <ImageNotFound
                        height={350}
                        width="80%"
                      />
                    )}
                    className={classes.itemImage}
                    skeletonClassName={classes.itemImage}
                    width="80%"
                    minHeight={350}
                    loading="lazy"
                  />
                </Box>
              )}
              {isPdf && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Card
                    sx={{
                      width: '80%',
                      mt: 2,
                      mb: 0,
                      flexShrink: 0,
                    }}
                  >
                    <CardMedia
                      sx={{
                        '&.MuiCardMedia-root': {
                          height: '700px',
                        },
                      }}
                      component="iframe"
                      controls
                      src={attachedFileURL}
                    />
                  </Card>
                </Box>
              )}
              {!isPdf && (
                <Fragment>
                  {!isTranslatedTextDisplayed && htmlWithAnnotations && (
                    <FormGroup>
                      <FormControlLabel
                        checked={isAnnotatedTextDisplayed}
                        control={<Switch onChange={handleToggleAnnotatedTextDisplay} />}
                        label={t('results.switch_annotated_text')}
                      />
                    </FormGroup>
                  )}
                  {shouldDisplayHtmlWithAnnotations && (
                    <Box
                      sx={{ ...textSx, overflow: 'auto' }}
                    >
                      <div
                        className="html-with-tags"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: htmlWithAnnotations }}
                      />
                    </Box>
                  )}
                  {shouldDisplayTextTranslated && (
                    <Box sx={{ ...textSx, mb: 2, display: 'flex' }}>
                      <Box>
                        {textOriginal}
                      </Box>
                      <Box sx={{ ml: 5 }}>
                        {textTranslated}
                      </Box>
                    </Box>
                  )}
                  {shouldDisplayTextOriginal && (
                    <Box sx={{ ...textSx, mb: 2 }}>
                      {textOriginal}
                    </Box>
                  )}
                </Fragment>
              )}
            </Fragment>
          )}
          {!_.isEmpty(transcriptionJSON) && (
            <MediaWithTranscription
              faces={faces}
              frames={frames}
              mediaType={isVideo ? 'video' : 'audio'}
              mediaUrl={attachedFileURL}
              patchDocumentInComplete={patchDocumentInComplete}
              transcriptionJSON={transcriptionJSON}
              displayTranslatedSentences={isTranslatedTextDisplayed}
            />
          )}
        </Fragment>
      )}
    </Box>
  );
};

Document.propTypes = {
  disableTagsRefine: PropTypes.bool,
  displayTitle: PropTypes.bool,
  document: documentPropType.isRequired,
  handleGetDocumentComplete: PropTypes.func,
  handleToggleAnnotatedTextDisplay: PropTypes.func,
  handleToggleTranslatedTextDisplay: PropTypes.func,
  isAnnotatedTextDisplayed: PropTypes.bool,
  isCompleteDisplay: PropTypes.bool,
  isTranslatedTextDisplayed: PropTypes.bool,
  patchDocumentInComplete: PropTypes.func,
};

Document.defaultProps = {
  disableTagsRefine: false,
  displayTitle: true,
  handleGetDocumentComplete: null,
  handleToggleAnnotatedTextDisplay: null,
  handleToggleTranslatedTextDisplay: null,
  isAnnotatedTextDisplayed: false,
  isCompleteDisplay: false,
  isTranslatedTextDisplayed: false,
  patchDocumentInComplete: null,
};

export default Document;
